// KBE-817: exact addition and subtraction for numbers a researcher typed.
//
// A transition config is stored as a DISTANCE from its boundary and resolved back
// to an absolute value by adding it again, so the same pair of numbers makes both
// trips. In binary floating point neither trip is clean -- `5.6 - 5.4` is
// 0.1999999999999993 and `5.6 - 0.2` is 5.3999999999999995 -- and both ends of
// that are user-visible: the stored distance shows up in the network diff, the
// release note and the variable Lineage tab, and the resolved value shows up in
// the editor field the researcher typed 5.4 into.
//
// Researcher-entered values are decimal literals, so decimal arithmetic is the
// faithful model for them: scale both operands by 10^k where k is the larger
// decimal place count, round to clear the multiply's own representation error,
// combine as integers, then scale back. Exact for such inputs, and it makes the
// round trip exact too (5.4 -> 0.2 -> 5.4).
//
// Deliberately not `limitNumberPrecision`, which rounds a float result to a
// guessed number of significant digits. That is data-dependent, and for this use
// it is unsafe in one specific way: rounding could push a 5% value UP past its
// boundary and so manufacture the negative distance the delta representation
// exists to make impossible. Scaled-integer arithmetic cannot.

function decimalPlaces(value: number): number {
  const text = String(value)
  // `String` switches to exponent notation outside ~1e-7..1e21, where the digits
  // after the point no longer count the decimal places on their own.
  const scientific = /^-?\d+(?:\.(\d+))?e([+-]\d+)$/i.exec(text)
  if (scientific) {
    return Math.max(0, (scientific[1]?.length ?? 0) - Number(scientific[2]))
  }
  const pointIndex = text.indexOf('.')
  return pointIndex === -1 ? 0 : text.length - pointIndex - 1
}

// Falls back to plain float arithmetic once scaling would leave the exactly
// representable integer range, rather than returning a silently wrong scaled
// result. Clinical values stay far inside it; the fallback keeps the function
// total for anything that does not.
function combine(a: number, b: number, sign: 1 | -1): number {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return a + sign * b
  const scale = 10 ** Math.max(decimalPlaces(a), decimalPlaces(b))
  const scaledA = Math.round(a * scale)
  const scaledB = Math.round(b * scale)
  if (!Number.isSafeInteger(scaledA) || !Number.isSafeInteger(scaledB)) {
    return a + sign * b
  }
  return (scaledA + sign * scaledB) / scale
}

export function addDecimal(a: number, b: number): number {
  return combine(a, b, 1)
}

export function subtractDecimal(a: number, b: number): number {
  return combine(a, b, -1)
}

// Needed for the one product in this area that becomes a STORED value: a
// percentage resolved against a boundary (`resolveAbsoluteBand`). Plain
// multiplication makes 5% of 5.6 into 0.27999999999999997, and a stored distance
// carrying that noise does not survive the editor's resolve -> store round trip --
// it comes back as 0.280000000000001, so editing one transition rewrites the
// stored value of every untouched row and reports a diff whose two sides render
// identically.
//
// Scales each operand separately, since the product's decimal places are the SUM
// of the operands' rather than the max.
export function multiplyDecimal(a: number, b: number): number {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return a * b
  const scaleA = 10 ** decimalPlaces(a)
  const scaleB = 10 ** decimalPlaces(b)
  const scaledA = Math.round(a * scaleA)
  const scaledB = Math.round(b * scaleB)
  if (!Number.isSafeInteger(scaledA) || !Number.isSafeInteger(scaledB)) {
    return a * b
  }
  const product = scaledA * scaledB
  // The product of two safe integers is not itself always safe.
  if (!Number.isSafeInteger(product)) return a * b
  return product / (scaleA * scaleB)
}
